import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import Modal from '../components/shared/Modal';
import EmptyState from '../components/shared/EmptyState';
import StatusBadge from '../components/shared/StatusBadge';
import { Key, Plus, Trash2, Upload } from 'lucide-react';

export default function KeyVault() {
  const { t } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [filterProduct, setFilterProduct] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState({ product_id: '', key_value: '' });
  const [bulkForm, setBulkForm] = useState({ product_id: '', keys_text: '' });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterProduct) params.product_id = filterProduct;
      if (filterStatus) params.status = filterStatus;

      const [keysRes, productsRes] = await Promise.all([
        api.get('/keys', { params }),
        api.get('/products'),
      ]);
      setKeys(keysRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  }, [filterProduct, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/keys', form);
      setModalOpen(false);
      setForm({ product_id: '', key_value: '' });
      fetchData();
    } catch (error) { console.error('Add failed:', error); }
  };

  const handleBulkAdd = async (e) => {
    e.preventDefault();
    const keysArray = bulkForm.keys_text.split('\n').map((k) => k.trim()).filter(Boolean);
    if (keysArray.length === 0) return;

    try {
      const response = await api.post('/keys/bulk', { product_id: bulkForm.product_id, keys: keysArray });
      alert(response.data.message);
      setBulkModalOpen(false);
      setBulkForm({ product_id: '', keys_text: '' });
      fetchData();
    } catch (error) { console.error('Bulk add failed:', error); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('keys.delete_confirm'))) return;
    try { await api.delete(`/keys/${id}`); fetchData(); } catch (error) { alert(error.response?.data?.error || t('keys.delete_failed')); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('keys.title')}</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => { setBulkForm({ product_id: products[0]?.id || '', keys_text: '' }); setBulkModalOpen(true); }} className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <Upload className="h-4 w-4" /> {t('keys.bulk_import')}
          </button>
          <button onClick={() => { setForm({ product_id: products[0]?.id || '', key_value: '' }); setModalOpen(true); }} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
            <Plus className="h-4 w-4" /> {t('keys.add')}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <select value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)} className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm">
          <option value="">{t('keys.all_products')}</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm">
          <option value="">{t('keys.all_statuses')}</option>
          <option value="available">{t('status.available')}</option>
          <option value="issued">{t('status.issued')}</option>
          <option value="blocked">{t('status.blocked')}</option>
        </select>
        <span className="text-sm text-gray-500">{t('keys.count', { count: keys.length })}</span>
      </div>

      {keys.length === 0 ? (
        <EmptyState icon={Key} title={t('keys.no_results')} description={t('keys.no_results_hint')} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('keys.key')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('keys.product')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('filters.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('keys.source')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('keys.issued_at')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {keys.map((key) => (
                <tr key={key.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{key.key_value}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{key.product?.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={key.status} /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{key.source}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{key.issued_at ? new Date(key.issued_at).toLocaleString() : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {key.status !== 'issued' && (
                      <button onClick={() => handleDelete(key.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={t('keys.add_title')}>
        <form onSubmit={handleAdd} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('keys.product')} *</label><select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('keys.license_key')} *</label><input type="text" value={form.key_value} onChange={(e) => setForm({ ...form, key_value: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 font-mono" placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX" /></div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">{t('common.cancel')}</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{t('keys.add')}</button>
          </div>
        </form>
      </Modal>

      <Modal open={bulkModalOpen} onClose={() => setBulkModalOpen(false)} title={t('keys.bulk_title')}>
        <form onSubmit={handleBulkAdd} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('keys.product')} *</label><select value={bulkForm.product_id} onChange={(e) => setBulkForm({ ...bulkForm, product_id: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('keys.keys_one_per_line')} *</label><textarea value={bulkForm.keys_text} onChange={(e) => setBulkForm({ ...bulkForm, keys_text: e.target.value })} required rows={10} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 font-mono text-sm" placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX&#10;YYYYY-YYYYY-YYYYY-YYYYY-YYYYY" /></div>
          <p className="text-sm text-gray-500">{t('keys.to_import', { count: bulkForm.keys_text.split('\n').filter(Boolean).length })}</p>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setBulkModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">{t('common.cancel')}</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{t('keys.import_keys')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
